import { api, time } from "@vuumly-common/common";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import {
  useFormatNumber,
  useParseRideLength,
  useQuery,
  useTheme,
  useTimeGranularity,
  useTranslate,
} from "hooks";
import _ from "lodash";
import { useMemo } from "react";
import { RoutePathEnum } from "../../../controllers/router";
import { BaseBarChart } from "../../Charts";
import { TimeGranularityTabs } from "../../Tabs/TimeGranularityTabs";
import { TextH5 } from "../../Typography/Text";
import { BaseCard } from "../Base.card";
import { CardProps } from "../index";

export const LengthGroupCompanies = (props: CardProps) => {
  const { disableLink = true } = props;
  const { formatShort } = useFormatNumber();
  const { getCompanyColors } = useTheme();
  const parseRideLenght = useParseRideLength();

  const translate = useTranslate();

  const { data, isLoading } = useQuery({
    apiEndpoint: api.Endpoint.LengthGroupCompanies,
  });

  const clonedData = useMemo(() => {
    if (isLoading || !data?.datasets) {
      return;
    }
    const parsedData = _.cloneDeep(data) as ChartData<"bar", Record<any, any>>;

    parsedData.labels = parsedData.datasets.flatMap((obj) =>
      obj.data
        .filter((item: any) => "dimension" in item)
        .map((item: any) => item.dimension)
    );

    parsedData.labels = _.uniq(parsedData.labels);

    parsedData.datasets.forEach((dataset) => {
      const dimension = dataset.data.map((item: any) => item.dimension);

      dataset.data =
        parsedData.labels?.map((item: any) => {
          if (!dimension.includes(item)) {
            return { dimension: item, ride_count: 0 };
          } else {
            return dataset.data.find((data: any) => data.dimension === item);
          }
        }) || [];

      dataset.label = `Company ${dataset.dimensions?.company_id}`;
      const colors = getCompanyColors(dataset.dimensions?.company_id);

      dataset.order = -dataset.data.reduce(
        (acc: number, item: any) => item.ride_count + acc,
        0
      );
      dataset.backgroundColor = colors.color;
      dataset.hoverBackgroundColor = colors.color;
      dataset.borderColor = "transparent";
      dataset.borderWidth = {
        top: 2,
        right: 0,
        bottom: 0,
        left: 0,
      };
    });

    return parsedData;
  }, [data]);

  const options: ChartOptions<"line"> = {
    parsing: {
      xAxisKey: "dimension",
      yAxisKey: "ride_count",
    },

    elements: {
      bar: {
        borderRadius: 0,
      },
    },

    scales: {
      x: {
        // type: "timeseries",
        stacked: true,
        ticks: {
          callback: (_a, idx) => {
            if (clonedData?.labels) {
              return parseRideLenght(
                (clonedData.labels[idx] as string) || "no_label"
              );
            }
          },
        },
      },
      y: {
        max: 100,
        title: {
          text: "Market share",
        },
        stacked: true,

        ticks: {
          callback: (value) => {
            return `${value}%`;
          },
        },
      },
    },

    plugins: {
      stacked100: { enable: true },
      tooltip: {
        itemSort: (a: any, b: any) => {
          return b.dataset.order - a.dataset.order;
        },
        callbacks: {
          label: (item: TooltipItem<"line">) => {
            const raw = item.raw as Record<string, any>;
            return `Company ${
              item.dataset.dimensions?.company_id
            }: ${formatShort(raw.ride_count)} (${item.formattedValue}%)`;
          },
        },
      },
    },
  };

  const Header = () => {
    return (
      <div className="flex justify-between w-full">
        <TextH5>{translate("Market share over time by company")}</TextH5>
      </div>
    );
  };

  return (
    <BaseCard
      isLoading={isLoading}
      header={<Header />}
      href={disableLink ? undefined : RoutePathEnum.Rides}
    >
      <BaseBarChart options={options} data={clonedData} />
    </BaseCard>
  );
};
